import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PlatformFeature {
  id: string;
  platform: string;
  category: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  display_order: number;
  is_active: boolean;
}

interface CategoryFeatures {
  category: string;
  features: PlatformFeature[];
}

export default function Features() {
  const [activeTab, setActiveTab] = useState('Codee');
  const [allFeatures, setAllFeatures] = useState<PlatformFeature[]>([]);
  const [heroSection, setHeroSection] = useState<any>(null);
  const [ctaSection, setCtaSection] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      // Fetch hero section
      const { data: heroData } = await supabase
        .from('hero_sections')
        .select('*')
        .eq('page', 'features')
        .eq('is_active', true)
        .single();

      if (heroData) setHeroSection(heroData);

      // Fetch platform features
      const { data, error } = await supabase
        .from('platform_features')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setAllFeatures(data || []);

      // Fetch CTA section
      const { data: ctaData } = await supabase
        .from('cta_sections')
        .select('*')
        .eq('page', 'features')
        .eq('is_active', true)
        .single();

      if (ctaData) setCtaSection(ctaData);
    } catch (error) {
      console.error('Error loading features:', error);
    } finally {
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Platform Features - Codee, Fusion & BI Solutions",
    "description": "Explore comprehensive features of our enterprise platforms: Codee low-code development, Fusion data integration, and BI analytics solutions.",
    "url": `${import.meta.env.VITE_SITE_URL}/features`
  };

  const getFeaturesByPlatform = (platform: string): CategoryFeatures[] => {
    const platformFeatures = allFeatures.filter(f => f.platform === platform);
    const categories = [...new Set(platformFeatures.map(f => f.category))];
    
    return categories.map(category => ({
      category,
      features: platformFeatures.filter(f => f.category === category)
    }));
  };

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      teal: 'bg-teal-100 text-teal-600',
      blue: 'bg-blue-100 text-blue-600',
      indigo: 'bg-indigo-100 text-indigo-600',
      purple: 'bg-purple-100 text-purple-600',
      green: 'bg-green-100 text-green-600',
      orange: 'bg-orange-100 text-orange-600',
      pink: 'bg-pink-100 text-pink-600',
      red: 'bg-red-100 text-red-600'
    };
    return colorMap[color] || 'bg-gray-100 text-gray-600';
  };

  const currentFeatures = getFeaturesByPlatform(activeTab);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Features - Comprehensive Platform Capabilities | Allgo Digital"
        description="Discover the powerful features of our integrated platform ecosystem including low-code development, data integration, and business intelligence tools."
        keywords="platform features, low-code tools, data integration, BI tools, enterprise features"
        canonical="/features"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-green-600 py-12 sm:py-16 lg:py-20"
        style={heroSection?.background_image ? {
          backgroundImage: `linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(147, 51, 234, 0.9), rgba(34, 197, 94, 0.9)), url('${heroSection.background_image}')`
        } : {
          backgroundImage: `linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(147, 51, 234, 0.9), rgba(34, 197, 94, 0.9)), url('https://public.readdy.ai/ai/img_res/f4961e379047db894b53654023a4d697.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
            {heroSection?.title || 'Powerful Features for Every Platform'}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 sm:mb-8 max-w-4xl mx-auto px-4">
            {heroSection?.subtitle || 'Discover the comprehensive capabilities that make our platforms the choice for enterprise development, data integration, and business intelligence.'}
          </p>
          <div className="flex justify-center px-4">
            <Link to={heroSection?.cta_link || '/contact'} className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-lg">
              {heroSection?.cta_text || 'Schedule Demo'}
            </Link>
          </div>
        </div>
      </section>

      {/* Platform Tabs */}
      <section className="py-8 sm:py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-8 sm:mb-12 overflow-x-auto">
            <div className="bg-white rounded-xl p-2 shadow-lg border inline-flex min-w-max">
              <div className="flex space-x-1 sm:space-x-2">
                <button
                  onClick={() => setActiveTab('Codee')}
                  className={`px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap ${
                    activeTab === 'Codee'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <i className="ri-code-s-slash-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Codee Platform</span>
                  <span className="sm:hidden">Codee</span>
                </button>
                <button
                  onClick={() => setActiveTab('Fusion')}
                  className={`px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap ${
                    activeTab === 'Fusion'
                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <i className="ri-flow-chart mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Fusion Platform</span>
                  <span className="sm:hidden">Fusion</span>
                </button>
                <button
                  onClick={() => setActiveTab('BI')}
                  className={`px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap ${
                    activeTab === 'BI'
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  <i className="ri-bar-chart-box-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">BI Platform</span>
                  <span className="sm:hidden">BI</span>
                </button>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          {loading ? (
            <div className="text-center py-12 sm:py-20">
              <div className="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 mt-4 text-base sm:text-lg">Loading features...</p>
            </div>
          ) : currentFeatures.length === 0 ? (
            <div className="text-center py-12 sm:py-20">
              <i className="ri-inbox-line text-5xl sm:text-6xl text-gray-300"></i>
              <p className="text-gray-600 mt-4 text-base sm:text-lg">No features available for this platform yet.</p>
            </div>
          ) : (
            <div className="space-y-12 sm:space-y-16">
              {currentFeatures.map((categoryGroup, categoryIndex) => (
                <div key={categoryIndex} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
                  <div className="text-center mb-8 sm:mb-12">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                      {categoryGroup.category}
                    </h2>
                    <div className={`w-20 sm:w-24 h-1 mx-auto rounded-full ${
                      activeTab === 'Codee' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                      activeTab === 'Fusion' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                      'bg-gradient-to-r from-green-500 to-green-600'
                    }`}></div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                    {categoryGroup.features.map((feature) => (
                      <div 
                        key={feature.id} 
                        className="group bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:transform hover:scale-105"
                      >
                        <div className="flex items-start space-x-3 sm:space-x-4">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClasses(feature.color)} group-hover:scale-110 transition-transform duration-300`}>
                            <i className={`${feature.icon} text-lg sm:text-xl`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-base sm:text-lg font-semibold mb-1 sm:mb-2 group-hover:transition-colors ${
                              activeTab === 'Codee' ? 'text-gray-900 group-hover:text-blue-600' :
                              activeTab === 'Fusion' ? 'text-gray-900 group-hover:text-purple-600' :
                              'text-gray-900 group-hover:text-green-600'
                            }`}>
                              {feature.title}
                            </h3>
                            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-12 sm:py-16 lg:py-20 ${
        activeTab === 'Codee' ? 'bg-gradient-to-r from-blue-600 to-blue-700' :
        activeTab === 'Fusion' ? 'bg-gradient-to-r from-purple-600 to-purple-700' :
        'bg-gradient-to-r from-green-600 to-green-700'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 px-4">
            {ctaSection?.title || 'Ready to Experience These Features?'}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            {ctaSection?.subtitle || 'See how our comprehensive platform features can transform your enterprise development and analytics capabilities.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to={ctaSection?.primary_cta_link || '/contact'} className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              {ctaSection?.primary_cta_text || 'Schedule Demo'}
            </Link>
            <Link to={ctaSection?.secondary_cta_link || '/platforms'} className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              {ctaSection?.secondary_cta_text || 'Explore Platforms'}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-10 sm:h-12 w-auto mb-3 sm:mb-4 filter brightness-0 invert"
              />
              <p className="text-sm sm:text-base text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Platforms</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li>info@allgo-digital.com</li>
                <li>+60 3-7931 8888</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-sm sm:text-base text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
